import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SectionTitle from "@/components/SectionTitle";
import AnalysisModal from "@/components/AnalysisModal";
import { Bot, Sparkles, LogIn, RefreshCw, Activity, TrendingUp, Zap, Gauge, Layers } from "lucide-react";
import { motion } from "framer-motion";

const FILTERS = [
  { id: "reclaim", label: "Reclaim VWAP", fn: (s) => s.reclaim_vwap },
  { id: "break", label: "Break ORH + Volume", fn: (s) => s.break_orh && s.vol_spike },
  { id: "above", label: "Di atas VWAP", fn: (s) => s.above_vwap },
  { id: "all", label: "Semua", fn: () => true },
];

const rupiah = (n) => (n == null ? "-" : "Rp " + Number(n).toLocaleString("id-ID"));

const Badge = ({ icon: Icon, label, cls }) => (
  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold ${cls}`}><Icon size={9} /> {label}</span>
);

const SignalCard = ({ s, onAnalyze }) => {
  const up = (s.change_pct ?? 0) >= 0;
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} onClick={() => onAnalyze(s.symbol)} data-testid={`robot-signal-${s.symbol}`} className="card p-4 cursor-pointer hover:border-[var(--accent)]/40 transition-colors">
      <div className="flex items-start justify-between mb-2">
        <div className="min-w-0">
          <p className="font-heading font-bold text-accent text-lg leading-none">{s.symbol}</p>
          <p className="text-[11px] text-[var(--text-muted)] truncate max-w-[150px] mt-0.5">{s.name}</p>
        </div>
        <div className="text-right">
          <p className="font-mono-data font-bold">{rupiah(s.price)}</p>
          <p className={`font-mono-data text-xs font-semibold ${up ? "text-bull" : "text-bear"}`}>{up ? "▲ +" : "▼ "}{s.change_pct}%</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-1 mb-3">
        {s.reclaim_vwap && <Badge icon={TrendingUp} label="Reclaim VWAP" cls="bg-[var(--bull)]/15 text-bull" />}
        {s.break_orh && <Badge icon={Zap} label="Break ORH" cls="bg-[var(--accent)]/15 text-accent" />}
        {s.vol_spike && <Badge icon={Activity} label="Vol Spike" cls="bg-[var(--amber)]/15 text-amber" />}
        {s.above_vwap && !s.reclaim_vwap && <Badge icon={Gauge} label="> VWAP" cls="bg-[var(--surface-hover)] text-[var(--text-secondary)]" />}
      </div>
      <div className="grid grid-cols-4 gap-2 text-center">
        {[["Sebelumnya", rupiah(s.prev_close)], ["VWAP", rupiah(s.vwap)], ["ORH", rupiah(s.orh)], ["Rel Vol", s.rel_vol == null ? "-" : s.rel_vol + "x"]].map(([k, v], i) => (
          <div key={i} className="bg-[var(--surface-base)] rounded-md py-1.5 border border-[var(--border-subtle)]">
            <p className="text-[9px] text-[var(--text-muted)] uppercase">{k}</p>
            <p className={`font-mono-data text-[11px] font-bold ${k === "Rel Vol" && s.rel_vol >= 1.3 ? "text-amber" : ""}`}>{v}</p>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

const wibTime = (iso) => {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleTimeString("id-ID", { timeZone: "Asia/Jakarta", hour: "2-digit", minute: "2-digit" }) + " WIB"; } catch { return "-"; }
};

export default function RobotAISection() {
  const { user, login } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [needLogin, setNeedLogin] = useState(false);
  const [filter, setFilter] = useState("reclaim");
  const [ticker, setTicker] = useState(null);
  const [showSectors, setShowSectors] = useState(false);
  const [showSignals, setShowSignals] = useState(false);

  const load = async () => {
    if (!user) { setNeedLogin(true); return; }
    setLoading(true); setNeedLogin(false);
    try {
      const res = await api.get("/robot");
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 401) setNeedLogin(true);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  const active = FILTERS.find((f) => f.id === filter) || FILTERS[0];
  const signals = (data?.signals || []).filter(active.fn);
  const sectors = data?.sectors || [];
  const maxAbs = Math.max(1, ...sectors.map((s) => Math.abs(s.avg_change)));
  const counts = {
    reclaim: (data?.signals || []).filter((s) => s.reclaim_vwap).length,
    break: (data?.signals || []).filter((s) => s.break_orh && s.vol_spike).length,
    above: (data?.signals || []).filter((s) => s.above_vwap).length,
    all: (data?.signals || []).length,
  };

  return (
    <section id="robot" className="scroll-mt-20">
      <SectionTitle eyebrow="Pemantau Intraday Otomatis" title="Robot AI" desc="Robot memantau tren sektor, harga sesi sebelumnya, reclaim VWAP, dan break Opening Range High (ORH) disertai volume — pada 90+ saham likuid IDX." icon={Bot} />

      {needLogin ? (
        <div className="card p-12 text-center" data-testid="robot-login-prompt">
          <Bot size={28} className="text-accent mx-auto mb-2" />
          <p className="text-sm text-[var(--text-secondary)] mb-4">Masuk untuk mengaktifkan Robot AI pemantau intraday.</p>
          <button onClick={login} className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-[var(--accent)] text-[#07090E] font-semibold hover:bg-[var(--accent-hover)]"><LogIn size={16} /> Masuk dengan Google</button>
        </div>
      ) : loading && !data ? (
        <div className="card py-16 text-center"><div className="w-9 h-9 border-2 border-[var(--accent)] border-t-transparent rounded-full animate-spin mx-auto mb-3" /><p className="text-sm text-[var(--text-secondary)]">Robot AI memindai pasar...</p></div>
      ) : (
        <div className="space-y-4">
          {/* AI summary */}
          <div className="card p-5 relative overflow-hidden" data-testid="robot-summary">
            <div className="absolute -top-20 -right-16 w-[260px] h-[260px] rounded-full bg-[var(--accent)]/8 blur-[90px]" />
            <div className="relative flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-[var(--accent)]/12 border border-[var(--accent)]/25 flex items-center justify-center shrink-0"><Bot size={22} className="text-accent" /></div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-heading font-bold">Pandangan Robot</span>
                  <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-[var(--accent)]/15 text-accent font-bold"><Sparkles size={9} /> AI</span>
                  <span className="text-[11px] text-[var(--text-muted)]">· scan {wibTime(data?.scan_at)}</span>
                  <button onClick={load} data-testid="robot-refresh" className="ml-auto inline-flex items-center gap-1 text-[11px] text-[var(--text-secondary)] hover:text-accent"><RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh</button>
                </div>
                <p className="text-sm text-[var(--text-secondary)] mt-1.5 leading-relaxed" data-testid="robot-ai-text">{data?.ai_summary || "Ringkasan Robot belum tersedia."}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Sector trend */}
            <div className="card p-5 lg:col-span-1" data-testid="robot-sectors">
              <p className="font-heading font-bold flex items-center gap-2 mb-4"><Layers size={16} className="text-accent" /> Tren Sektor</p>
              <div className="space-y-2.5">
                {(showSectors ? sectors : sectors.slice(0, 3)).map((s) => {
                  const pos = s.avg_change >= 0;
                  return (
                    <div key={s.sector} className="text-xs">
                      <div className="flex justify-between mb-1">
                        <span className="text-[var(--text-secondary)] truncate max-w-[150px]">{s.sector}</span>
                        <span className={`font-mono-data font-bold ${pos ? "text-bull" : "text-bear"}`}>{pos ? "+" : ""}{s.avg_change}%</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-[var(--surface-base)] relative overflow-hidden">
                        <div className={`h-full rounded-full ${pos ? "bg-[var(--bull)]" : "bg-[var(--bear)]"}`} style={{ width: `${(Math.abs(s.avg_change) / maxAbs) * 100}%` }} />
                      </div>
                      <p className="text-[9px] text-[var(--text-muted)] mt-0.5">{s.up} naik · {s.down} turun · {s.count} saham</p>
                    </div>
                  );
                })}
              </div>
              {sectors.length > 3 && (
                <button onClick={() => setShowSectors((v) => !v)} data-testid="sectors-more" className="mt-3 w-full h-8 rounded-lg bg-[var(--surface-base)] border border-[var(--border)] text-xs font-semibold text-accent hover:bg-[var(--surface-hover)] transition-colors">
                  {showSectors ? "Tampilkan lebih sedikit" : `Selengkapnya (${sectors.length - 3} sektor)`}
                </button>
              )}
            </div>

            {/* Signals */}
            <div className="lg:col-span-2">
              <div className="flex flex-wrap gap-2 mb-3">
                {FILTERS.map((f) => (
                  <button key={f.id} onClick={() => { setFilter(f.id); setShowSignals(false); }} data-testid={`robot-filter-${f.id}`} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${filter === f.id ? "bg-[var(--accent)] text-[#07090E]" : "bg-[var(--surface-base)] border border-[var(--border)] text-[var(--text-secondary)] hover:text-accent"}`}>
                    {f.label} <span className="opacity-70">({counts[f.id]})</span>
                  </button>
                ))}
              </div>
              {signals.length === 0 ? (
                <div className="card p-10 text-center text-sm text-[var(--text-muted)]">Tidak ada sinyal untuk filter ini saat scan terakhir.</div>
              ) : (
                <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(showSignals ? signals : signals.slice(0, 3)).map((s) => <SignalCard key={s.symbol} s={s} onAnalyze={setTicker} />)}
                </div>
                {signals.length > 3 && (
                  <button onClick={() => setShowSignals((v) => !v)} data-testid="signals-more" className="mt-3 w-full h-9 rounded-lg bg-[var(--surface-base)] border border-[var(--border)] text-sm font-semibold text-accent hover:bg-[var(--surface-hover)] transition-colors">
                    {showSignals ? "Tampilkan lebih sedikit" : `Selengkapnya (${signals.length - 3} sinyal lainnya)`}
                  </button>
                )}
                </>
              )}
            </div>
          </div>
          <p className="text-[10px] text-[var(--text-muted)]">VWAP & ORH dihitung dari data intraday 5 menit; di luar jam bursa memakai sesi terakhir. Edukasi, bukan ajakan jual/beli. DYOR.</p>
        </div>
      )}
      {ticker && <AnalysisModal ticker={ticker} onClose={() => setTicker(null)} />}
    </section>
  );
}
